/**
 * TrackDetailPanel — bottom-of-screen detail card that appears when the
 * operator clicks a drone on the live map. Mirrors the data already being
 * drawn on the map (identity, altitude, speed, classification, pilot), so
 * the panel is effectively a zero-fetch projection of mapStore state.
 *
 * Why no new API call: the live-tracks poll already ships every field we
 * want to render. A second `tracksApi.getTrack()` request would lag behind
 * live motion and add a round-trip for no new information.
 *
 * The panel also exposes a "Replay flight" shortcut that flips the map
 * into historical replay for this track — same entry point used by the
 * `?replay=<trackId>` query param.
 */
import { useMemo } from 'react';
import {
  XMarkIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useMapStore } from '../../store/mapStore';
import { TrackClassification } from '@airrecon/shared';
import StreetViewPanel from './StreetViewPanel';
import { useTerrainElevation, type TerrainElevation } from '../../utils/terrainElevation';

// Human-readable classification labels. Kept local to the panel so a future
// tweak for a single screen doesn't drag along every other surface.
const CLASSIFICATION_LABEL: Record<TrackClassification, string> = {
  [TrackClassification.UNKNOWN]: 'Unknown',
  [TrackClassification.DRONE_SMALL]: 'Drone (small)',
  [TrackClassification.DRONE_MEDIUM]: 'Drone (medium)',
  [TrackClassification.DRONE_LARGE]: 'Drone (large)',
  [TrackClassification.AIRCRAFT_GENERAL]: 'Aircraft',
  [TrackClassification.AIRCRAFT_COMMERCIAL]: 'Commercial aircraft',
  [TrackClassification.HELICOPTER]: 'Helicopter',
  [TrackClassification.BIRD]: 'Bird',
  [TrackClassification.WEATHER]: 'Weather',
  [TrackClassification.CLUTTER]: 'Clutter',
};

// Small helper — "12,345 ft" with graceful fallback.
function metersToFeet(m?: number): string {
  if (!m && m !== 0) return '—';
  return `${Math.round(m * 3.28084).toLocaleString()} ft`;
}

// Render the terrain-elevation hook's state into a "12,345 ft" string,
// "…" while sampling, or "Unavailable" when terrain is missing.
function formatElevation(state: TerrainElevation): string {
  if (state.status === 'loading') return '…';
  if (state.status === 'unavailable') return 'Unavailable';
  return metersToFeet(state.meters);
}

// Broadcast altitude minus ground elevation, only when both are known.
function formatAGL(
  broadcastMeters: number | undefined,
  state: TerrainElevation,
): string {
  if (state.status === 'loading') return '…';
  if (state.status === 'unavailable') return 'Unavailable';
  if (broadcastMeters === undefined || broadcastMeters === null) return '—';
  return metersToFeet(broadcastMeters - state.meters);
}

// 123.4 m/s → "276 mph" (ground speed is how operators think about it).
function mpsToMph(mps?: number): string {
  if (!mps && mps !== 0) return '—';
  return `${Math.round(mps * 2.23694).toLocaleString()} mph`;
}

// Heading like "075° (E)". Lowercase direction for compactness.
function formatHeading(deg?: number): string {
  if (deg === undefined || deg === null || Number.isNaN(deg)) return '—';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(((deg % 360) + 360) / 45) % 8;
  return `${Math.round(deg).toString().padStart(3, '0')}° ${dirs[idx]}`;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  /** Dim when the value is unknown so the rows don't compete for attention. */
  muted?: boolean;
}

function DetailRow({ label, value, muted }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-[110px]">
      <span className="text-[10px] uppercase tracking-wider text-gray-500">
        {label}
      </span>
      <span
        className={
          'text-sm font-medium ' + (muted ? 'text-gray-500' : 'text-white')
        }
      >
        {value}
      </span>
    </div>
  );
}

export default function TrackDetailPanel() {
  const selectedTrackId = useMapStore((s) => s.selectedTrackId);
  const setSelectedTrack = useMapStore((s) => s.setSelectedTrack);
  const liveTracks = useMapStore((s) => s.liveTracks);
  const setReplayTrackId = useMapStore((s) => s.setReplayTrackId);
  const setLayers = useMapStore((s) => s.setLayers);
  const flyTo = useMapStore((s) => s.flyTo);

  // Pull the currently-selected track fresh on every render so values stay
  // in sync with the 2-s live poll without any extra subscription wiring.
  const track = useMemo(
    () => (selectedTrackId ? liveTracks.get(selectedTrackId) : undefined),
    [selectedTrackId, liveTracks],
  );

  // Sample Cesium World Terrain under the drone's GPS position. Cached
  // by ~100m precision so the drone moving doesn't re-query each tick.
  // Hook is called unconditionally (rules of hooks); when no track is
  // selected the inputs are undefined and the hook returns 'unavailable'.
  const elevation = useTerrainElevation(track?.latitude, track?.longitude);

  // Collapse the panel entirely when nothing is selected or the track has
  // aged out of the live set (e.g. flight ended mid-inspection).
  if (!selectedTrackId || !track) return null;

  // Identity title — same precedence rule as the on-map label:
  // serial > registration > callsign > trackId suffix.
  const titleParts: string[] = [];
  if (track.serialNumber) titleParts.push(track.serialNumber);
  else if (track.registration) titleParts.push(track.registration);
  else if (track.callsign) titleParts.push(track.callsign);
  else titleParts.push(track.trackId.slice(-8).toUpperCase());

  const meta = (track.metadata as Record<string, unknown> | undefined) ?? {};
  const hasOperator =
    typeof meta.operatorLatitude === 'number' &&
    typeof meta.operatorLongitude === 'number' &&
    !(meta.operatorLatitude === 0 && meta.operatorLongitude === 0);

  const handleReplay = () => {
    setReplayTrackId(track.trackId);
    setLayers({ historicalTracks: true });
  };

  const handleCenter = () => {
    // Nudge the camera to the drone's current position so the panel
    // doubles as a "where is this drone" shortcut.
    flyTo(track.longitude, track.latitude, Math.max(track.altitudeMSL + 3000, 5000));
  };

  return (
    <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-20 w-[min(96vw,960px)]">
      <div className="bg-navy-900/95 backdrop-blur-md border border-navy-700 rounded-xl shadow-2xl px-4 py-3">
        {/* Header: identity + close */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-accent font-semibold">
                Selected Track
              </span>
              <span
                className="text-[10px] uppercase tracking-wider text-gray-500"
              >
                ·
              </span>
              <span className="text-[10px] uppercase tracking-wider text-gray-400">
                {CLASSIFICATION_LABEL[track.classification] ?? track.classification}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-gray-500">
                · confidence {Math.round((track.confidence ?? 0) * 100)}%
              </span>
            </div>
            <h2 className="text-lg font-display font-semibold text-white truncate">
              {titleParts[0]}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCenter}
              className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white bg-navy-800/80 border border-navy-700 rounded px-2.5 py-1.5 transition-colors"
              title="Center map on this drone"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Center
            </button>
            <button
              type="button"
              onClick={handleReplay}
              className="flex items-center gap-1.5 text-xs text-navy-900 bg-accent hover:bg-accent/90 rounded px-2.5 py-1.5 font-semibold transition-colors"
              title="Replay this track's flight history"
            >
              <PlayIcon className="h-3.5 w-3.5" />
              Replay flight
            </button>
            <button
              type="button"
              onClick={() => setSelectedTrack(null)}
              className="p-1.5 text-gray-500 hover:text-white transition-colors"
              aria-label="Close track details"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          {/* Altitude trio:
              - Broadcast altitude is what the drone reports via Remote ID
                (could be MSL, could be HOME-relative, depends on vendor).
              - Ground elevation is the DEM value under the drone's GPS fix.
              - Calculated AGL = broadcast − ground; useful sanity check
                when the drone's broadcast reference is uncertain. */}
          <DetailRow
            label="Broadcast Altitude (Remote ID)"
            value={metersToFeet(track.altitudeMSL)}
          />
          <DetailRow
            label="Ground Elevation (Terrain)"
            value={formatElevation(elevation)}
            muted={elevation.status !== 'ready'}
          />
          <DetailRow
            label="Calculated AGL"
            value={formatAGL(track.altitudeMSL, elevation)}
            muted={elevation.status !== 'ready'}
          />
          <DetailRow label="Ground speed" value={mpsToMph(track.groundSpeed)} />
          <DetailRow label="Heading" value={formatHeading(track.heading)} />
          <DetailRow
            label="Serial"
            value={track.serialNumber ?? '—'}
            muted={!track.serialNumber}
          />
          <DetailRow
            label="Registration"
            value={track.registration ?? '—'}
            muted={!track.registration}
          />
          <DetailRow
            label="Manufacturer / Model"
            value={
              track.manufacturer || track.model
                ? `${track.manufacturer ?? '—'}${track.model ? ` / ${track.model}` : ''}`
                : '—'
            }
            muted={!track.manufacturer && !track.model}
          />
          <DetailRow
            label="Callsign / ICAO"
            value={
              track.callsign || track.icaoAddress
                ? `${track.callsign ?? '—'}${track.icaoAddress ? ` / ${track.icaoAddress.toUpperCase()}` : ''}`
                : '—'
            }
            muted={!track.callsign && !track.icaoAddress}
          />
          <DetailRow
            label="Pilot location"
            value={
              hasOperator
                ? `${(meta.operatorLatitude as number).toFixed(4)}, ${(meta.operatorLongitude as number).toFixed(4)}`
                : '—'
            }
            muted={!hasOperator}
          />
        </div>

        {/* Elevation-source attribution. Two purposes: tell operators the
            calculated AGL came from independent terrain data (not the
            drone's own broadcast), and surface the failure reason when
            terrain is unavailable so it doesn't look like a UI bug. */}
        <p className="mt-2 text-[10px] text-gray-500">
          {elevation.status === 'ready'
            ? `Calculated AGL derived from ${elevation.source} (DEM).`
            : elevation.status === 'loading'
              ? 'Sampling terrain elevation…'
              : elevation.reason}
        </p>

        {/* Street View pilot's-eye panel. Only renders when we actually
            know where the pilot is — without operator coords a Street
            View pane is meaningless and would confuse the operator. */}
        {hasOperator && (
          <StreetViewPanel
            pilotLat={meta.operatorLatitude as number}
            pilotLng={meta.operatorLongitude as number}
            targetLat={track.latitude}
            targetLng={track.longitude}
          />
        )}
      </div>
    </div>
  );
}
